import { type Path, setByPath } from '@clickbar/dot-diver';
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Group,
  LoadingOverlay,
  Space,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowLeft,
  IconCheck,
  IconCopy,
  IconInfoHexagon,
  IconPlus,
  IconRocket,
  IconZoomExclamation,
} from '@tabler/icons-react';
import { type Edge, Panel, ReactFlowProvider } from '@xyflow/react';
import type Graph from 'graphology';
import type { HighsSolution } from 'highs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { v4 } from 'uuid';
import { useFormOnChange } from '@/core/form/useFormOnChange';
import { loglev } from '@/core/logger/log';
import { useStore } from '@/core/zustand';
import {
  useFactoryInputsOutputs,
  useFactorySimpleAttributes,
} from '@/factories/store/factoriesSelectors';
import { GameSettingsModal } from '@/games/settings/GameSettingsModal';
import { AfterHeaderSticky } from '@/layout/AfterHeaderSticky';
import { isByproductNode } from '@/solver/algorithm/getSolutionNodes';
import type { SolverContext } from '@/solver/algorithm/SolverContext';
import type { SolverEdge, SolverNode } from '@/solver/algorithm/SolverNode';
import { isSolutionFound } from '@/solver/algorithm/solve/isSolutionFound';
import {
  type SolutionNode,
  solveProduction,
  useHighs,
} from '@/solver/algorithm/solveProduction';
import { SolverInspectorDrawer } from '@/solver/inspector/SolverInspectorDrawer';
import { SolverLayout } from '@/solver/layout/SolverLayout';
import { SolverSolutionProvider } from '@/solver/layout/solution-context/SolverSolutionContext';
import { SolverLayoutButtons } from '@/solver/layout/state/SolverLayoutButtons';
import { SolverShareButton } from '@/solver/share/SolverShareButton';
import type { SolverInstance } from '@/solver/store/Solver';
import {
  useCurrentSolverId,
  usePathSolverInstance,
  useSolverGameId,
} from '@/solver/store/solverSelectors';
import { SolverRequestDrawer } from './request-drawer/SolverRequestDrawer';
import { SolverResetButton } from './SolverResetButton';
import {
  type ISolverSolutionSuggestion,
  proposeSolverSolutionSuggestions,
} from './suggestions/proposeSolverSolutionSuggestions';
import { SolverSuggestions } from './suggestions/SolverSuggestions';
import { SolverSummarySidebar } from './summary/SolverSummarySidebar';
import { useSolverSummaryStats } from './summary/useSolverSummaryStats';

const logger = loglev.getLogger('solver:page');

export interface ISolverPageProps {}

// TODO Move in dedicated file
export interface ISolverSolution {
  result: HighsSolution;
  nodes: SolutionNode[];
  edges: Edge[];
  graph: Graph<SolverNode, SolverEdge, any>;
  context: SolverContext;
}

export function SolverPage(props: ISolverPageProps) {
  const params = useParams<{ id: string }>();
  const id = params.id;

  const { highsRef, loading } = useHighs();
  const navigate = useNavigate();

  const factory = useFactorySimpleAttributes(id);
  const inputsOutputs = useFactoryInputsOutputs(id);
  const instance = usePathSolverInstance();
  // This is not the _displayed_ solver ID, but the one that is to be used if no solver ID is provided
  const currentSolverId = useCurrentSolverId();
  const solverGameId = useSolverGameId(id);

  useEffect(() => {
    if (!params.id) return;
    if (instance && factory?.id) return;

    logger.info('SolverPage: No instance or factory, creating', id);
    useStore.getState().upsertFactorySolver(id, {
      inputs: [],
      outputs: [
        {
          resource: 'Desc_Cement_C',
          amount: 20,
        },
      ],
    });
  }, [instance, factory, id, params.id, navigate]);

  useEffect(() => {
    if (params.id && params.id !== currentSolverId) {
      useStore.getState().setCurrentSolver(params.id);
    }
  }, [params.id, currentSolverId]);

  const updater = useMemo(
    () => (path: Path<SolverInstance>, value: string | null | number) => {
      useStore.getState().updateSolver(id!, state => {
        setByPath(state, path, value);
      });
    },
    [id],
  );

  const onChangeHandler = useFormOnChange<SolverInstance>(updater);

  /**
   * This is the main entry point for the solver algorithm.
   * It will compute the solution and suggestions based on the current
   * instance and inputs/outputs.
   */
  const { solution, suggestions } = useMemo(() => {
    let suggestions: ISolverSolutionSuggestion = {};
    if (!instance?.request || !highsRef.current || loading) {
      return {
        solution: null,
        suggestions,
      };
    }

    const solution = solveProduction(highsRef.current, {
      ...instance?.request,
      ...inputsOutputs,
      nodes: instance.nodes,
    });
    logger.log(`Solved -> `, solution);

    if (solution && !isSolutionFound(solution)) {
      suggestions = proposeSolverSolutionSuggestions(
        highsRef.current,
        instance.request,
        inputsOutputs,
      );
    }

    logger.log('hasSolution =', isSolutionFound(solution));

    return { solution, suggestions };
    // We don't want to re-run computation if instance changes, only if its request changes
  }, [highsRef, instance?.request, instance?.nodes, inputsOutputs, loading]);

  useEffect(() => {
    if (!id || !solution || !isSolutionFound(solution)) return;
    const outputs = useStore.getState().factories.factories[id]?.outputs;
    if (!outputs) return;
    const maximizedNodes = solution.nodes
      .filter(isByproductNode)
      .filter(
        n => n.data.output?.objective === 'max' && n.data.outputIndex != null,
      );
    for (const node of maximizedNodes) {
      const outputIndex = node.data.outputIndex;
      if (outputIndex == null) continue;
      if (outputs[outputIndex]?.computedAmount === node.data.value) continue;
      useStore.getState().updateFactoryOutput(id, outputIndex, {
        computedAmount: node.data.value,
      });
    }
  }, [id, solution]);

  if (params.id == null) {
    if (currentSolverId) {
      logger.log('No solver ID, redirecting to', currentSolverId);
      navigate(`/factories/${currentSolverId}/calculator`);
    } else {
      logger.log('No solver ID, creating');
      const newSolverId = v4();
      useStore.getState().setCurrentSolver(newSolverId);
      navigate(`/factories/${newSolverId}/calculator`);
    }
  }

  const hasSolution = isSolutionFound(solution);

  const [sidebarOpen, { toggle: toggleSidebar }] = useDisclosure(false);

  const stats = useSolverSummaryStats(hasSolution ? solution : null);

  const [copied, setCopied] = useState(false);
  const copyDebugInfo = useCallback(() => {
    const debug = {
      factoryId: id,
      factory: factory
        ? {
            name: factory.name,
            inputs: inputsOutputs?.inputs,
            outputs: inputsOutputs?.outputs,
          }
        : null,
      solverRequest: instance?.request ?? null,
      nodeStates: instance?.nodes ?? null,
      solution: solution
        ? {
            status: solution.result?.Status,
            nodes: solution.nodes.map(n => ({
              id: n.id,
              type: n.type,
              data: n.data,
            })),
            edges: solution.edges.map(e => ({
              id: e.id,
              source: e.source,
              target: e.target,
              data: e.data,
            })),
          }
        : null,
    };
    navigator.clipboard.writeText(JSON.stringify(debug, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [id, factory, inputsOutputs, instance, solution]);

  return (
    <Box w="100%" pos="relative">
      <LoadingOverlay visible={loading} />

      <AfterHeaderSticky>
        <Group gap="sm" justify="space-between">
          <Group gap="sm">
            {solverGameId && (
              <>
                <Button
                  component={Link}
                  to="/factories"
                  variant="light"
                  color="gray"
                  leftSection={<IconArrowLeft size={16} />}
                >
                  All Factories
                </Button>
              </>
            )}
            <Title order={4}>
              <TextInput
                value={factory?.name ?? 'Solver'}
                placeholder="Factory Name"
                onChange={e => {
                  useStore
                    .getState()
                    .updateFactory(
                      factory.id,
                      f => (f.name = e.currentTarget.value),
                    );
                }}
              />
            </Title>
            {!solverGameId && id && (
              <Button
                variant="filled"
                onClick={() => {
                  useStore.getState().addFactoryIdToGame(undefined, id);
                }}
                leftSection={<IconPlus size={16} />}
              >
                Add to Game
              </Button>
            )}
            <SolverResetButton id={id} factory={factory} />
          </Group>
          <Group gap="sm">
            <SolverRequestDrawer
              solution={solution}
              onSolverChangeHandler={onChangeHandler}
            />

            <GameSettingsModal />
          </Group>
        </Group>
      </AfterHeaderSticky>
      {solution && hasSolution && (
          <Flex style={{ height: '80vh' }}>
            {stats && sidebarOpen && (
              <SolverSummarySidebar
                stats={stats}
                onClose={toggleSidebar}
              />
            )}
            <Box flex={1} style={{ minWidth: 0 }}>
              <ReactFlowProvider>
                <SolverSolutionProvider solution={solution}>
                  <SolverLayout nodes={solution.nodes} edges={solution.edges}>
                    <Panel>
                      <Group gap="xs">
                        {/* Primary actions */}
                        <Button
                          size="sm"
                          variant={sidebarOpen ? 'light' : 'filled'}
                          leftSection={<IconInfoHexagon size={16} />}
                          onClick={toggleSidebar}
                        >
                          Summary
                        </Button>
                        <SolverShareButton />

                        <Divider
                          orientation="vertical"
                          color="dark.4"
                          mx={2}
                        />

                        {/* Layout controls */}
                        <SolverLayoutButtons solution={solution} />

                        <Button
                          variant="subtle"
                          size="xs"
                          color={copied ? 'teal' : 'gray'}
                          leftSection={
                            copied ? (
                              <IconCheck size={14} />
                            ) : (
                              <IconCopy size={14} />
                            )
                          }
                          onClick={copyDebugInfo}
                        >
                          {copied ? 'Copied' : 'Copy Debug Info'}
                        </Button>
                        {import.meta.env.DEV && (
                          <SolverInspectorDrawer solution={solution} />
                        )}
                      </Group>
                    </Panel>
                  </SolverLayout>
                </SolverSolutionProvider>
              </ReactFlowProvider>
            </Box>
          </Flex>
      )}
      {!hasSolution && (
        <Container size="lg" mt="lg">
          <Stack gap="xs" align="center" mih={200} mt={60} mb={90}>
            <IconZoomExclamation size={64} stroke={1.2} />
            <Text fz="h2">No results found</Text>
            <Text size="sm" c="dark.2">
              No solution found for the given parameters.
            </Text>
            <Space h="xs" />
            <Group gap="xs">
              <IconRocket
                size={20}
                stroke={1.5}
                color="var(--mantine-color-blue-4)"
              />
              <Text size="sm" c="dimmed">
                Configure your inputs and outputs above, then select
                available recipes to find a production plan.
              </Text>
            </Group>
            <Space />
            <SolverSuggestions suggestions={suggestions} instance={instance} />
          </Stack>
        </Container>
      )}
    </Box>
  );
}
